import React, { useMemo } from 'react'
import type { AccessContext, Organization, RelationshipAccessEntry } from '../../../types/access'
import { countByOrgType } from './adminPanelUtils'
import { formatTenantLabel } from '../../../utils/displayLabels'
import { useI18n } from '../../../i18n/I18nContext'
import { useIndustryTerminology } from '../../../hooks/useIndustryTerminology'

interface AdminOverviewPanelProps {
  accessContext: AccessContext | null
  organizations: Organization[]
  selectedOrgId?: string
  canNavigateTo: (tabId: string) => boolean
  onNavigate: (tabId: string) => void
  onOpenRelationship?: (relationshipId: string) => void
  onViewClientLocations?: (entry: RelationshipAccessEntry) => void
  onCreateDelegatedJob?: (entry: RelationshipAccessEntry) => void
}

type DashboardAction = {
  label: string
  tabId?: string
  onClick?: () => void
  testId?: string
}

type DashboardMetric = {
  key: string
  label: string
  value: React.ReactNode
  definition: string
  action?: DashboardAction
  tone?: 'default' | 'attention' | 'positive'
}

const deriveConnectionState = (entry: RelationshipAccessEntry): string => {
  if (entry.connectionState) return entry.connectionState
  const status = entry.status
  if (status === 'revoked') return 'revoked'
  if (status === 'suspended') return 'suspended'
  if (status === 'pending') return 'pending_invite'
  if (status !== 'active') return status || 'no_link'
  if (!entry.companyOrg || !entry.agencyOrg) return 'invalid_missing_endpoint'
  if (!entry.hasPolicy && !entry.visibilityPreset) return 'linked_unconfigured'
  const grantCount = Number(entry.grantCount || 0)
  const permissions = entry.permissions || {}
  const delegated = Boolean(
    permissions.canCreateJobsOnBehalf
    || permissions.canCreateShiftsOnBehalf
    || permissions.canAssignWorkersOnBehalf
    || permissions.canSubmitTimesheets
  )
  if (grantCount > 0 && delegated) return 'delegated_connection'
  if (grantCount === 0 && permissions.canViewLocations) return 'policy_configured_no_locations'
  return 'built_connection'
}

const connectionStateMessageKey = (state: string): string => {
  if (state === 'pending_invite') return 'Invite sent. Waiting for partner to accept.'
  if (state === 'linked_unconfigured') return 'Connected, but no visibility policy has been configured.'
  if (state === 'policy_configured_no_locations') return 'Permissions configured, but no locations granted.'
  if (state === 'built_connection') return 'Connection active.'
  if (state === 'delegated_connection') return 'Delegated workforce access active.'
  if (state === 'suspended') return 'Connection suspended. Future access is paused.'
  if (state === 'revoked') return 'Connection revoked. Historical records remain available where permitted.'
  if (state === 'invalid_missing_endpoint') return 'Connection references a missing organization and needs platform repair.'
  return 'Connection status unknown.'
}

const connectionStateLabelKey = (state: string): string => {
  const known: Record<string, string> = {
    pending_invite: 'Pending invite',
    linked_unconfigured: 'Linked, unconfigured',
    policy_configured_no_locations: 'Policy configured, no locations',
    built_connection: 'Connection built',
    delegated_connection: 'Delegated connection',
    suspended: 'Suspended',
    revoked: 'Revoked',
    invalid_missing_endpoint: 'Missing organization',
    no_link: 'No link',
  }
  return known[state] || 'Connection status unknown.'
}

const hasActionableRelationshipState = (state: string) => (
  state === 'pending_invite'
  || state === 'linked_unconfigured'
  || state === 'policy_configured_no_locations'
  || state === 'suspended'
  || state === 'invalid_missing_endpoint'
)

export const AdminOverviewPanel: React.FC<AdminOverviewPanelProps> = ({
  accessContext,
  organizations,
  selectedOrgId,
  canNavigateTo,
  onNavigate,
  onOpenRelationship,
  onViewClientLocations,
  onCreateDelegatedJob,
}) => {
  const { t } = useI18n()
  const industryLabels = useIndustryTerminology()
  const organizationsByType = useMemo(() => countByOrgType(organizations), [organizations])
  const accessibleLocations = accessContext?.accessibleLocations || []
  const relationshipAccess: RelationshipAccessEntry[] = (accessContext?.relationshipAccess || []) as RelationshipAccessEntry[]
  const jobs = accessContext?.jobs
  const currentTenantId = accessContext?.tenantId
  const activeOrg = (selectedOrgId ? organizations.find((org) => org.id === selectedOrgId) : null)
    || accessContext?.currentOrganization
    || (accessContext?.isSystemSuperAdmin ? null : organizations[0])
    || null
  const currentOrgId = activeOrg?.id
  const activeRelationships = relationshipAccess.filter((entry) => deriveConnectionState(entry) !== 'revoked')
  const activePartners = activeRelationships.filter((entry) => {
    if (entry.isAgencyManagedProfile) return false
    if (currentOrgId && entry.companyOrg?.id !== currentOrgId && entry.agencyOrg?.id !== currentOrgId) return false
    return ['built_connection', 'delegated_connection', 'policy_configured_no_locations', 'linked_unconfigured'].includes(deriveConnectionState(entry))
  })
  const directStaffCount = useMemo(() => {
    if (!currentOrgId) return null
    const fromContext = accessContext?.ownStaffCountByOrgId?.[currentOrgId]
    return typeof fromContext === 'number' ? fromContext : undefined
  }, [accessContext?.ownStaffCountByOrgId, currentOrgId])
  const staffMetricValue = !currentOrgId
    ? t('Select an organization')
    : (directStaffCount === undefined ? '—' : directStaffCount)
  const staffMetricDefinition = !currentOrgId
    ? t('Select a tenant organization before viewing organization-owned staff.')
    : (directStaffCount === undefined
      ? t('Server did not return the organization-owned staff count for this org.')
      : t('direct active workers in this organization. Partner-visible, shared, and assigned workers are excluded.'))
  const networkVisibleWorkers = accessContext?.workerOrgRelationships?.length ?? 0
  const freeAgentCount = accessContext?.workerSharing?.freeAgentVisibility?.visibleToAllCount ?? 0
  const companyConnections = useMemo(
    () => relationshipAccess.filter((entry) => currentOrgId && entry.companyOrg?.id === currentOrgId && !entry.isAgencyManagedProfile),
    [relationshipAccess, currentOrgId],
  )
  const agencyConnections = useMemo(
    () => relationshipAccess.filter((entry) => currentOrgId && entry.agencyOrg?.id === currentOrgId && !entry.isAgencyManagedProfile),
    [relationshipAccess, currentOrgId],
  )
  const managedProfiles = useMemo(
    () => relationshipAccess.filter((entry) => entry.isAgencyManagedProfile),
    [relationshipAccess],
  )
  const scopedRelationshipRows = activeOrg?.org_type === 'company'
    ? companyConnections
    : activeOrg?.org_type === 'agency'
      ? agencyConnections
      : activePartners
  const visibleLocationsLabel = accessibleLocations.length === 1
    ? t('1 visible location')
    : `${accessibleLocations.length} ${t('visible locations')}`
  const scopeLabel = [
    formatTenantLabel(accessContext?.tenant, accessContext?.tenantId),
    activeOrg?.name || t('No organization selected'),
    accessibleLocations.length ? visibleLocationsLabel : t('tenant scope'),
  ].filter(Boolean).join(' / ')
  const actionFor = (label: string, tabId: string): DashboardAction | undefined => (
    canNavigateTo(tabId) ? { label, tabId } : undefined
  )
  const invokeAction = (action: DashboardAction) => {
    if (action.onClick) {
      action.onClick()
      return
    }
    if (action.tabId) onNavigate(action.tabId)
  }
  const renderActionButton = (action: DashboardAction, className = 'network-card-action') => (
    <button
      key={`${action.label}-${action.tabId || action.testId || 'action'}`}
      type="button"
      className={className}
      data-testid={action.testId}
      onClick={() => invokeAction(action)}
    >
      {action.label}
    </button>
  )
  const renderMetric = (metric: DashboardMetric) => (
    <article key={metric.key} className={`network-kpi-card network-kpi-card--${metric.tone || 'default'} network-metric-card`}>
      <span className="helper">{metric.label}</span>
      <strong>{metric.value}</strong>
      <span className="network-kpi-definition">{metric.definition}</span>
      {metric.action ? renderActionButton(metric.action) : null}
    </article>
  )

  const overviewMetrics: DashboardMetric[] = [
    {
      key: 'own-staff',
      label: industryLabels.explicitIndustry ? `Own ${industryLabels.label('workers')}` : t('Own staff'),
      value: staffMetricValue,
      definition: staffMetricDefinition,
      action: actionFor(industryLabels.explicitIndustry ? `Open ${industryLabels.label('workers')}` : t('Open Staff'), 'workers'),
    },
    {
      key: 'network-visible',
      label: t('Network-visible workers'),
      value: networkVisibleWorkers,
      definition: t('Active worker relationship rows visible in this tenant or scoped user context. This is not the Staff count.'),
      action: actionFor(industryLabels.explicitIndustry ? `Open ${industryLabels.label('workers')}` : t('Open Staff'), 'workers'),
    },
    {
      key: 'open-jobs',
      label: industryLabels.explicitIndustry ? `Open ${industryLabels.label('jobs')}` : t('Open jobs'),
      value: jobs ? jobs.openJobsCount : '—',
      definition: t('Accessible jobs with posted, open, or partially filled status.'),
      action: actionFor(industryLabels.explicitIndustry ? `Open ${industryLabels.label('jobs')}` : t('Open Jobs'), 'jobs'),
    },
    {
      key: 'pending-approvals',
      label: t('Pending approvals'),
      value: jobs ? (jobs.pendingAssignmentsCount + jobs.pendingApplicationsCount) : '—',
      definition: t('Accessible submitted or shortlisted applications plus assignments pending worker or company approval.'),
      action: actionFor(t('Review'), 'jobs'),
      tone: jobs && (jobs.pendingAssignmentsCount + jobs.pendingApplicationsCount) > 0 ? 'attention' : 'positive',
    },
    {
      key: 'locations',
      label: t('Authorized locations'),
      value: accessibleLocations.length,
      definition: t('Locations returned in your access context; supervisors only see authorized location scope.'),
      action: actionFor(industryLabels.explicitIndustry ? `Open ${industryLabels.label('locations')}` : t('Open Locations'), 'locations'),
    },
    {
      key: 'partners',
      label: t('Active partners'),
      value: activePartners.length,
      definition: t('Active company-agency relationships in the selected organization scope, excluding managed legacy profiles.'),
      action: actionFor(t('Open Relationships'), 'relationships'),
    },
  ]

  const attentionItems = [
    jobs && jobs.pendingAssignmentsCount > 0 ? {
      key: 'pending-assignments',
      title: t('Assignments need approval'),
      detail: `${jobs.pendingAssignmentsCount} ${t('pending worker or company assignment approvals')}`,
      action: actionFor(t('Open Jobs'), 'jobs'),
    } : null,
    jobs && jobs.pendingApplicationsCount > 0 ? {
      key: 'pending-applications',
      title: t('Applications need review'),
      detail: `${jobs.pendingApplicationsCount} ${t('submitted or shortlisted applications')}`,
      action: actionFor(t('Open Jobs'), 'jobs'),
    } : null,
    ...activeRelationships
      .filter((entry) => hasActionableRelationshipState(deriveConnectionState(entry)))
      .slice(0, 3)
      .map((entry) => {
        const state = deriveConnectionState(entry)
        const partnerName = activeOrg?.org_type === 'agency'
          ? entry.companyOrg?.name
          : entry.agencyOrg?.name || entry.companyOrg?.name
        return {
          key: `relationship-${entry.relationshipId}`,
          title: t(connectionStateLabelKey(state)),
          detail: `${partnerName || t('Relationship')} · ${t(connectionStateMessageKey(state))}`,
          action: canNavigateTo('relationships')
            ? {
              label: t('Open relationship'),
              onClick: () => {
                if (onOpenRelationship) onOpenRelationship(entry.relationshipId)
                else onNavigate('relationships')
              },
            }
            : undefined,
        }
      }),
  ].filter(Boolean) as Array<{ key: string; title: string; detail: string; action?: DashboardAction }>

  const quickActions: DashboardAction[] = [
    actionFor(industryLabels.explicitIndustry ? `Open ${industryLabels.label('jobs')}` : t('Open Jobs'), 'jobs'),
    actionFor(t('Open Schedule'), 'schedule'),
    actionFor(industryLabels.explicitIndustry ? `Open ${industryLabels.label('workers')}` : t('Open Staff'), 'workers'),
    actionFor(t('Open Live Map'), 'live-map'),
    actionFor(t('Open Relationships'), 'relationships'),
  ].filter(Boolean).slice(0, 5) as DashboardAction[]

  const renderRelationshipRow = (entry: RelationshipAccessEntry) => {
    const state = deriveConnectionState(entry)
    const isAgencyView = activeOrg?.org_type === 'agency'
    const partner = isAgencyView ? entry.companyOrg : entry.agencyOrg || entry.companyOrg
    const isFederated = entry.isFederated || (
      isAgencyView
        ? entry.companyTenantId && entry.companyTenantId !== currentTenantId
        : entry.agencyTenantId && entry.agencyTenantId !== currentTenantId
    )
    const canManage = Boolean(entry.canManagePolicy || entry.canManageGrants)
    const openWorkspaceAction: DashboardAction = isAgencyView
      ? {
        label: t('Open workspace'),
        testId: 'open-client-company-workspace',
        onClick: () => onViewClientLocations ? onViewClientLocations(entry) : onNavigate('locations'),
      }
      : {
        label: t('Open workspace'),
        testId: 'open-workforce-partner-workspace',
        onClick: () => {
          if (onOpenRelationship) onOpenRelationship(entry.relationshipId)
          else onNavigate('relationships')
        },
      }
    const secondaryActions: DashboardAction[] = [openWorkspaceAction]
    if (entry.permissions?.canCreateJobsOnBehalf && isAgencyView) {
      secondaryActions.push({
        label: industryLabels.explicitIndustry ? `Create ${industryLabels.label('job').toLowerCase()}` : t('Create job'),
        onClick: () => onCreateDelegatedJob ? onCreateDelegatedJob(entry) : onNavigate('jobs'),
      })
    }
    if (canManage && canNavigateTo('relationships')) {
      secondaryActions.push({
        label: t('Configure'),
        testId: 'configure-relationship-btn',
        onClick: () => {
          if (onOpenRelationship) onOpenRelationship(entry.relationshipId)
          else onNavigate('relationships')
        },
      })
    }
    if (entry.canTerminate && canNavigateTo('relationships')) {
      secondaryActions.push({
        label: t('Suspend / Revoke'),
        testId: 'suspend-relationship-btn',
        onClick: () => {
          if (onOpenRelationship) onOpenRelationship(entry.relationshipId)
          else onNavigate('relationships')
        },
      })
    }
    return (
      <li
        key={entry.relationshipId}
        className="network-relationship-row"
        data-testid={isAgencyView ? 'client-company-card' : 'workforce-partner-card'}
      >
        <div>
          <strong>{partner?.name || t('Linked partner')}</strong>
          <span className="helper">{t(connectionStateMessageKey(state))}</span>
        </div>
        <div className="network-row-meta">
          {isFederated ? <span className="status-pill status-pill--muted">{t('Federated partner')}</span> : null}
          {!canManage && isAgencyView ? <span className="status-pill status-pill--muted">{t('Read-only')}</span> : null}
          <span className="status-pill">{t(connectionStateLabelKey(state))}</span>
          <span className="helper">{t('Sites')}: {entry.grantCount ?? entry.grantedLocations?.length ?? 0}</span>
        </div>
        <div className="network-row-actions">
          {secondaryActions.slice(0, 3).map((action) => renderActionButton(action, action.testId ? 'btn btn-ghost' : 'network-card-action'))}
        </div>
      </li>
    )
  }

  return (
    <div className="network-dashboard stack" data-testid="admin-dashboard-panel">
      <section className="network-dashboard-hero">
        <div>
          <h3>{t('Network operations overview')}</h3>
          <p className="text-muted">{scopeLabel}</p>
        </div>
        <div className="network-quick-actions" aria-label={t('Dashboard quick actions')}>
          {quickActions.map((action) => renderActionButton(action, 'btn btn-ghost'))}
        </div>
      </section>

      <section className="network-dashboard-section network-attention-section" aria-labelledby="network-attention-title">
        <div className="network-section-heading">
          <div>
            <h4 id="network-attention-title">{t('Attention required')}</h4>
            <p className="helper">{t('Only real approval or relationship states from your authorized context are shown here.')}</p>
          </div>
          {attentionItems.length === 0 ? <span className="status-pill status-pill--approved">{t('No immediate action')}</span> : null}
        </div>
        {attentionItems.length > 0 ? (
          <ul className="network-attention-list">
            {attentionItems.map((item) => (
              <li key={item.key} className="network-attention-item">
                <div>
                  <strong>{item.title}</strong>
                  <span className="helper">{item.detail}</span>
                </div>
                {item.action ? renderActionButton(item.action) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="network-positive-state">{t('No pending approvals or relationship setup issues are visible in this scope.')}</p>
        )}
      </section>

      <section className="network-dashboard-section" aria-labelledby="network-overview-title">
        <div className="network-section-heading">
          <div>
            <h4 id="network-overview-title">{t('Operational overview')}</h4>
            <p className="helper">{t('Compact metrics backed by the current access context and scoped backend summaries.')}</p>
          </div>
        </div>
        <div className="network-kpi-grid">
          {overviewMetrics.map(renderMetric)}
        </div>
      </section>

      <section className="network-dashboard-section" aria-labelledby="network-composition-title">
        <div className="network-section-heading">
          <div>
            <h4 id="network-composition-title">{t('Network composition')}</h4>
            <p className="helper">{t('Own staff and network-visible workforce stay separated so partner supply never reads as Staff.')}</p>
          </div>
        </div>
        <div className="network-composition-grid">
          <article>
            <span className="helper">{t('Organization mix')}</span>
            <strong>{organizationsByType.company || 0} {t('companies')} · {organizationsByType.agency || 0} {t('agencies')}</strong>
            <span className="helper">{t('Visible organizations returned by your access context.')}</span>
          </article>
          <article>
            <span className="helper">{t('Own workers')}</span>
            <strong>{staffMetricValue}</strong>
            <span className="helper">{staffMetricDefinition}</span>
          </article>
          <article>
            <span className="helper">{t('Partner / network workforce')}</span>
            <strong>{networkVisibleWorkers}</strong>
            <span className="helper">{t('Network-visible worker relationship rows; not counted as organization-owned staff.')}</span>
          </article>
          <article>
            <span className="helper">{t('Free-agent visibility')}</span>
            <strong>{freeAgentCount}</strong>
            <span className="helper">{t('Limited-profile free agents visible through the sharing model.')}</span>
          </article>
        </div>
      </section>

      <section className="network-dashboard-section" aria-labelledby="network-work-title">
        <div className="network-section-heading">
          <div>
            <h4 id="network-work-title">{t('Work and coverage')}</h4>
            <p className="helper">{t('High-level demand and approval signals only. Open the destination screen for detailed scheduling, staffing, and timesheets.')}</p>
          </div>
        </div>
        <div className="network-work-grid">
          {renderMetric({
            key: 'work-open-jobs',
            label: industryLabels.explicitIndustry ? `${industryLabels.label('jobs')} requiring coverage` : t('Work requiring coverage'),
            value: jobs ? jobs.openJobsCount : '—',
            definition: t('Accessible jobs in posted, open, or partially filled status.'),
            action: actionFor(t('Open Jobs'), 'jobs'),
          })}
          {renderMetric({
            key: 'work-pending-applications',
            label: t('Pending applications'),
            value: jobs ? jobs.pendingApplicationsCount : '—',
            definition: t('Submitted or shortlisted applications on accessible jobs.'),
            action: actionFor(t('Review'), 'jobs'),
            tone: jobs && jobs.pendingApplicationsCount > 0 ? 'attention' : 'positive',
          })}
          {renderMetric({
            key: 'work-pending-assignments',
            label: t('Pending assignments'),
            value: jobs ? jobs.pendingAssignmentsCount : '—',
            definition: t('Assignments awaiting worker acceptance or company approval.'),
            action: actionFor(t('Review'), 'jobs'),
            tone: jobs && jobs.pendingAssignmentsCount > 0 ? 'attention' : 'positive',
          })}
        </div>
      </section>

      {(scopedRelationshipRows.length > 0 || managedProfiles.length > 0) ? (
        <section
          className="network-dashboard-section"
          aria-labelledby="network-relationships-title"
          data-testid={activeOrg?.org_type === 'agency' ? 'client-companies-section' : 'workforce-partners-section'}
        >
          <div className="network-section-heading">
            <div>
              <h4 id="network-relationships-title">{activeOrg?.org_type === 'agency' ? t('Client Companies') : t('Workforce Partners')}</h4>
              <p className="helper">{t('A short operational list of partner relationships. Detailed policy, grants, and lifecycle controls stay in Network Relationships.')}</p>
            </div>
            {canNavigateTo('relationships') ? renderActionButton({ label: t('Open Relationships'), tabId: 'relationships' }, 'btn btn-ghost') : null}
          </div>
          {scopedRelationshipRows.length > 0 ? (
            <ul className="network-relationship-list">
              {scopedRelationshipRows.slice(0, 4).map(renderRelationshipRow)}
            </ul>
          ) : null}
          {managedProfiles.length > 0 ? (
            <div data-testid="managed-profiles-section" className="network-managed-profiles">
              <span className="helper">{t('Managed Client Profiles')}</span>
              <ul className="network-relationship-list">
                {managedProfiles.slice(0, 3).map((entry) => (
                  <li key={entry.relationshipId} className="network-relationship-row" data-testid="managed-profile-card">
                    <div>
                      <strong>{entry.companyOrg?.name || entry.agencyOrg?.name || t('Client profile')}</strong>
                      <span className="helper">{t('Claim status')}: {entry.claimStatus || t('unclaimed')}</span>
                    </div>
                    <span className="status-pill status-pill--muted">{t('Managed Client Profile')}</span>
                    {canNavigateTo('relationships') ? renderActionButton({
                      label: t('View'),
                      onClick: () => {
                        if (onOpenRelationship) onOpenRelationship(entry.relationshipId)
                        else onNavigate('relationships')
                      },
                    }) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  )
}
